import { getFirebaseSingleton } from "@lib/firebase";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { AppLoadingPage } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { store } from "@store/index";
import qs from "query-string";
import { ReactElement, useEffect } from "react";
import { Redirect, useLocation } from "react-router-dom";

import { GlobalRouterPath } from "./constant/globalRoute";

interface PublicPageProps {
  authStateHasLoaded: boolean;
  isAuthorized: boolean;
  children: ReactElement;
}

export function PublicPage(props: PublicPageProps): ReactElement {
  const { isAuthorized, authStateHasLoaded, children } = props;
  const location = useLocation();
  const queryString = qs.parse(location.search);
  const queryStringKeys = Object.keys(queryString);

  const isDebuggingEnabled = useCbhFlag(CbhFeatureFlag.WORKER_APP_INITIALIZATION_DEBUGGING, {
    defaultValue: false,
  });

  const { appLoadingPageLog } = useCbhFlag(CbhFeatureFlag.TRACK_INDEXEDDB_SPLASH_HANG, {
    defaultValue: {
      indexedDbOpenExperiment: false,
      appLoadingPageLog: {
        enabled: false,
        timeoutInMs: 10000,
      },
    },
  });

  useEffect(() => {
    if (isDebuggingEnabled) {
      const firebaseUser = getFirebaseSingleton().currentUser;
      const sessionState = store.getState().session;

      logEvent(APP_V2_APP_EVENTS.APP_LOADING_PAGE_PUBLIC_PAGE_PROPS, {
        isAuthorized,
        authStateHasLoaded,
        hasChildren: !!children,
        locationPathname: location.pathname,
        queryStringKeys,
        hasFirebaseUser: Boolean(firebaseUser),
        firebaseUserEmail: firebaseUser?.email,
        firebaseUserUid: firebaseUser?.uid,
        // Redux session state
        reduxIsAuthorized: sessionState.isAuthorized,
        reduxAuthStateHasLoaded: sessionState.authStateHasLoaded,
        reduxIsLoggingOut: sessionState.isLoggingOut,
        reduxIsVerifying: sessionState.isVerifying,
        reduxIsSignup: sessionState.isSignup,
        reduxUserId: sessionState.userId,
        reduxUserPhone: sessionState.profile?.phone,
        reduxHasEnv: Boolean(sessionState.env),
        reduxShowAlert: sessionState.showAlert,
        reduxActivePopups: Object.keys(sessionState.popups || {}).filter(
          (key) => sessionState.popups?.[key]?.visible
        ),
      });
    }
  }, [
    isDebuggingEnabled,
    isAuthorized,
    authStateHasLoaded,
    children,
    location.pathname,
    queryStringKeys,
  ]);

  if (!authStateHasLoaded) {
    return (
      <AppLoadingPage
        parentEventName={APP_V2_APP_EVENTS.APP_LOADING_PAGE_PUBLIC_PAGE_PROPS}
        isDebuggingEnabled={isDebuggingEnabled || appLoadingPageLog.enabled}
        debugTimeoutInMs={appLoadingPageLog.timeoutInMs}
      />
    );
  }
  if (isAuthorized) {
    return <Redirect to={(queryString.redirectUri as string) ?? GlobalRouterPath.HOME} />;
  }
  return children;
}
